import React, { useState, useEffect } from "react";
import { authFetch } from "../api";
import Header from "../components/Header";
import NeighborhoodSelect from "../components/NeighborhoodSelect";

const STATUSES = [
  { key: "TO_REVIEW", label: "New", color: "#e3f2fd" },
  { key: "IN_REVIEW", label: "In Review", color: "#fff3e0" },
  { key: "REVIEWED", label: "Reviewed", color: "#e8f5e9" },
  { key: "MIS", label: "MIS", color: "#fce4ec" },
  { key: "RETIRED", label: "Retired", color: "#f5f5f5" },
];

export default function SaleReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedItem, setDraggedItem] = useState(null);

  // Modal state for MIS/Retired transitions
  const [showModal, setShowModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [statusComment, setStatusComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Filter states
  const [searchAddress, setSearchAddress] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [bedsMin, setBedsMin] = useState("");
  const [bedsMax, setBedsMax] = useState("");
  const [bathsMin, setBathsMin] = useState("");
  const [bathsMax, setBathsMax] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [capRateMin, setCapRateMin] = useState("");
  const [capRateMax, setCapRateMax] = useState("");
  const [daysOnMarketMax, setDaysOnMarketMax] = useState("");
  const [propertyType, setPropertyType] = useState("all"); // "all", "condo", "coop"
  const [carryingCostMax, setCarryingCostMax] = useState(""); // HOA + TAX combined max
  const [sortBy, setSortBy] = useState("date_desc"); // sort option
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState([]); // Server-side filter (array)
  const [neighborhoods, setNeighborhoods] = useState([]); // Available neighborhoods
  const [defaultNeighborhoods, setDefaultNeighborhoods] = useState([]); // User's defaults
  // neighborhoodDropdownOpen state removed — handled by NeighborhoodSelect component
  const [totalCount, setTotalCount] = useState(0); // Total matching properties (before limit)
  // eslint-disable-next-line no-unused-vars
  const [serverLimit, setServerLimit] = useState(200); // Server-side limit

  const [neighborhoodsLoaded, setNeighborhoodsLoaded] = useState(false);

  useEffect(() => {
    loadNeighborhoods();
  }, []);

  useEffect(() => {
    // Load reviews once after neighborhoods are loaded (initial load with defaults)
    if (neighborhoodsLoaded) {
      loadReviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neighborhoodsLoaded]); // Only on initial load

  const loadNeighborhoods = async () => {
    try {
      const data = await authFetch("/sale-reviews/neighborhoods");
      setNeighborhoods(data.neighborhoods);

      // default_neighborhoods from DB could be IDs (integers or string numbers) or legacy names (strings)
      // Convert to integers
      let defaultIds = [];
      if (data.default_neighborhoods && data.default_neighborhoods.length > 0) {
        defaultIds = data.default_neighborhoods.map(item => {
          // If it's already a number, use it
          if (typeof item === 'number') return item;
          // If it's a string that looks like a number, parse it
          if (typeof item === 'string' && /^\d+$/.test(item)) return parseInt(item, 10);
          // Legacy string name - find the neighborhood by name
          const found = data.neighborhoods.find(n => n.name === item);
          return found ? found.id : null;
        }).filter(id => id !== null);
      }

      setDefaultNeighborhoods(defaultIds);
      if (defaultIds.length > 0) {
        setSelectedNeighborhoods(defaultIds);
      }
      setNeighborhoodsLoaded(true);
    } catch (error) {
      console.error("Error loading neighborhoods:", error);
      setNeighborhoodsLoaded(true); // Still mark as loaded so we don't block forever
    }
  };

  const loadReviews = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchAddress) {
        // When searching by address, skip neighborhood filter to search everywhere
        params.append("search", searchAddress);
      } else if (selectedNeighborhoods.length > 0) {
        params.append("neighborhood_ids", selectedNeighborhoods.join(","));
      }
      const url = `/sale-reviews${params.toString() ? `?${params.toString()}` : ""}`;
      const data = await authFetch(url);
      setReviews(data.reviews);
      setTotalCount(data.total_count || data.reviews.length);
      setServerLimit(data.limit || 200);
    } catch (error) {
      console.error("Error loading reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, review) => {
    setDraggedItem(review);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.status === newStatus) {
      setDraggedItem(null);
      return;
    }

    // If dropping to MIS or Retired, show modal for required comment
    if (newStatus === "MIS" || newStatus === "RETIRED") {
      setPendingStatus(newStatus);
      setShowModal(true);
      return; // Don't clear draggedItem yet
    }

    // For other statuses, update immediately
    await updateStatus(newStatus, `Status changed from ${draggedItem.status} to ${newStatus}`);
  };

  const updateStatus = async (newStatus, reason) => {
    if (!draggedItem) return;

    try {
      await authFetch(`/sale-reviews/${draggedItem.id}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus, reason }),
      });

      // Update local state
      setReviews(
        reviews.map((r) =>
          r.id === draggedItem.id ? { ...r, status: newStatus } : r
        )
      );
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Failed to update status");
    } finally {
      setDraggedItem(null);
    }
  };

  const handleModalConfirm = async () => {
    if (!statusComment.trim()) {
      alert("Please provide a reason for this status change.");
      return;
    }

    setSubmitting(true);
    await updateStatus(pendingStatus, statusComment);
    setSubmitting(false);

    // Close modal and reset
    setShowModal(false);
    setStatusComment("");
    setPendingStatus(null);
  };

  const handleModalCancel = () => {
    setShowModal(false);
    setStatusComment("");
    setPendingStatus(null);
    setDraggedItem(null);
  };

  // Filter reviews based on criteria
  const filterReviews = (reviewsList) => {
    return reviewsList.filter((review) => {
      // Address search is handled server-side now (via Search button)

      // Date range
      if (dateFrom && review.property.listed_at && new Date(review.property.listed_at) < new Date(dateFrom)) {
        return false;
      }
      if (dateTo && review.property.listed_at && new Date(review.property.listed_at) > new Date(dateTo)) {
        return false;
      }

      // Bedrooms (handle null values)
      const beds = review.property.bedrooms;
      if (bedsMin && (beds === null || beds === undefined || beds < parseInt(bedsMin))) {
        return false;
      }
      if (bedsMax && (beds === null || beds === undefined || beds > parseInt(bedsMax))) {
        return false;
      }

      // Bathrooms (handle null values)
      const baths = review.property.bathrooms;
      if (bathsMin && (baths === null || baths === undefined || baths < parseFloat(bathsMin))) {
        return false;
      }
      if (bathsMax && (baths === null || baths === undefined || baths > parseFloat(bathsMax))) {
        return false;
      }

      // Price
      if (priceMin && review.property.price < parseInt(priceMin)) {
        return false;
      }
      if (priceMax && review.property.price > parseInt(priceMax)) {
        return false;
      }

      // Property Type (DB values are uppercase: CONDO, COOP, CONDP, TOWNHOUSE)
      if (propertyType !== "all") {
        const propType = (review.property.property_type || "").toUpperCase();
        if (propertyType === "coop") {
          // Co-op: COOP and CONDP (condo-op)
          if (propType !== "COOP" && propType !== "CONDP") {
            return false;
          }
        } else if (propertyType === "condo") {
          // Condo: everything EXCEPT COOP and CONDP
          if (propType === "COOP" || propType === "CONDP") {
            return false;
          }
        }
      }

      // Cap Rate
      const capRate = review.property.cap_rate;
      if (capRateMin && (capRate === null || capRate === undefined || capRate < parseFloat(capRateMin))) {
        return false;
      }
      if (capRateMax && (capRate === null || capRate === undefined || capRate > parseFloat(capRateMax))) {
        return false;
      }

      // Days on Market (use pre-calculated active_dom, fallback to calendar days)
      if (daysOnMarketMax) {
        const dom = review.property.days_on_market != null
          ? review.property.days_on_market
          : review.property.listed_at
            ? Math.floor((Date.now() - new Date(review.property.listed_at).getTime()) / (1000 * 60 * 60 * 24))
            : null;
        if (dom != null && dom > parseInt(daysOnMarketMax)) {
          return false;
        }
      }

      // Carrying costs (HOA + Tax)
      if (carryingCostMax) {
        const carrying = (review.property.monthly_hoa || 0) + (review.property.monthly_tax || 0);
        if (carrying > parseInt(carryingCostMax)) {
          return false;
        }
      }

      return true;
    });
  };

  const handleClearFilters = () => {
    setSearchAddress("");
    setDateFrom("");
    setDateTo("");
    setBedsMin("");
    setBedsMax("");
    setBathsMin("");
    setBathsMax("");
    setPriceMin("");
    setPriceMax("");
    setCapRateMin("");
    setCapRateMax("");
    setDaysOnMarketMax("");
    setCarryingCostMax("");
    setPropertyType("all");
    // Reset to user's defaults if they have any, otherwise show all
    setSelectedNeighborhoods(defaultNeighborhoods.length > 0 ? defaultNeighborhoods : []);
  };

  const filteredReviews = filterReviews(reviews).sort((a, b) => {
    const pa = a.property, pb = b.property;
    switch (sortBy) {
      case "date_asc":
        return (new Date(pa.listed_at || 0)) - (new Date(pb.listed_at || 0));
      case "cap_desc":
        return (pb.cap_rate ?? -999) - (pa.cap_rate ?? -999);
      case "cap_asc":
        return (pa.cap_rate ?? 999) - (pb.cap_rate ?? 999);
      case "price_asc":
        return (pa.price ?? 999999999) - (pb.price ?? 999999999);
      case "price_desc":
        return (pb.price ?? 0) - (pa.price ?? 0);
      case "dom_asc":
        return (pa.days_on_market ?? 999) - (pb.days_on_market ?? 999);
      case "dom_desc":
        return (pb.days_on_market ?? 0) - (pa.days_on_market ?? 0);
      case "date_desc":
      default:
        return (new Date(pb.listed_at || 0)) - (new Date(pa.listed_at || 0));
    }
  });

  const groupedReviews = STATUSES.reduce((acc, status) => {
    acc[status.key] = filteredReviews.filter((r) => r.status === status.key);
    return acc;
  }, {});

  const pageStyle = {
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
  };

  const containerStyle = {
    padding: "20px",
    maxWidth: "1800px",
    margin: "0 auto",
  };

  const boardStyle = {
    display: "grid",
    gridTemplateColumns: `repeat(${STATUSES.length}, 1fr)`,
    gap: "20px",
    marginTop: "20px",
  };

  const columnStyle = (color) => ({
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "15px",
    minHeight: "500px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  });

  const columnHeaderStyle = (color) => ({
    fontSize: "18px",
    fontWeight: "600",
    marginBottom: "15px",
    padding: "10px",
    backgroundColor: color,
    borderRadius: "4px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  });

  const cardStyle = {
    backgroundColor: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: "6px",
    padding: "12px",
    marginBottom: "10px",
    cursor: "grab",
    transition: "all 0.2s",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  };

  const cardHoverStyle = {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
  };

  const addressStyle = {
    fontSize: "14px",
    fontWeight: "600",
    marginBottom: "8px",
    color: "#333",
  };

  const detailsStyle = {
    fontSize: "12px",
    color: "#666",
    marginBottom: "4px",
  };

  // Dynamic price style based on price change
  const getPriceStyle = (priceChange) => {
    let color = "#000"; // default black (no change)
    if (priceChange && priceChange < 0) {
      color = "#2e7d32"; // green for price drop (good for buyer)
    } else if (priceChange && priceChange > 0) {
      color = "#d32f2f"; // red for price increase (bad for buyer)
    }
    return {
      fontSize: "16px",
      fontWeight: "bold",
      color,
      marginTop: "8px",
    };
  };

  // Format price change for display
  const formatPriceChange = (change, pct) => {
    if (!change || change === 0) return null;
    const sign = change > 0 ? "+" : "-";
    const absChange = Math.abs(change);
    const formattedChange = absChange >= 1000
      ? `${sign}$${(absChange / 1000).toFixed(0)}K`
      : `${sign}$${absChange.toLocaleString()}`;
    const formattedPct = pct ? ` (${sign}${Math.abs(pct).toFixed(1)}%)` : "";
    return `${formattedChange}${formattedPct}`;
  };

  const badgeStyle = {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    padding: "2px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "500",
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <Header showLogout={true} />
        <div style={containerStyle}>
          <div style={{ textAlign: "center", marginTop: "50px" }}>
            Loading reviews...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <Header showLogout={true} />
      <div style={containerStyle}>
        {/* Filter Panel */}
        <div style={{
          padding: "16px",
          backgroundColor: "#fff",
          borderRadius: "8px",
          marginBottom: "20px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
        }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "12px",
            marginBottom: "12px"
          }}>
            {/* Neighborhood (Server-side filter - multi-select dropdown) */}
            <NeighborhoodSelect
              neighborhoods={neighborhoods}
              selected={selectedNeighborhoods}
              onChange={setSelectedNeighborhoods}
              defaults={defaultNeighborhoods}
              placeholder="All Neighborhoods (limit 200)"
              gridColumn="span 2"
            />

            {/* Search Address */}
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" }}>
                Address (use # for unit)
              </label>
              <input
                type="text"
                placeholder="Search by address or listing ID..."
                value={searchAddress}
                onChange={(e) => setSearchAddress(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") loadReviews(); }}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  fontSize: "13px"
                }}
              />
            </div>

            {/* Property Type */}
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" }}>
                Property Type
              </label>
              <select
                value={propertyType}
                onChange={(e) => setPropertyType(e.target.value)}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  fontSize: "13px",
                  backgroundColor: "white"
                }}
              >
                <option value="all">All Properties</option>
                <option value="condo">Condo</option>
                <option value="coop">Co-op</option>
              </select>
            </div>

            {/* Bedrooms */}
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" }}>
                Bedrooms
              </label>
              <div style={{ display: "flex", gap: "8px" }}>
                <input
                  type="number"
                  placeholder="Min"
                  value={bedsMin}
                  onChange={(e) => setBedsMin(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "6px 8px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "13px"
                  }}
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={bedsMax}
                  onChange={(e) => setBedsMax(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "6px 8px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "13px"
                  }}
                />
              </div>
            </div>

            {/* Bathrooms */}
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" }}>
                Bathrooms
              </label>
              <div style={{ display: "flex", gap: "8px" }}>
                <input
                  type="number"
                  step="0.5"
                  placeholder="Min"
                  value={bathsMin}
                  onChange={(e) => setBathsMin(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "6px 8px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "13px"
                  }}
                />
                <input
                  type="number"
                  step="0.5"
                  placeholder="Max"
                  value={bathsMax}
                  onChange={(e) => setBathsMax(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "6px 8px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "13px"
                  }}
                />
              </div>
            </div>

            {/* Price Range */}
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" }}>
                Price ($)
              </label>
              <div style={{ display: "flex", gap: "8px" }}>
                <input
                  type="number"
                  placeholder="Min"
                  value={priceMin}
                  onChange={(e) => setPriceMin(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "6px 8px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "13px"
                  }}
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={priceMax}
                  onChange={(e) => setPriceMax(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "6px 8px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "13px"
                  }}
                />
              </div>
            </div>

            {/* Cap Rate */}
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" }}>
                Cap Rate (%)
              </label>
              <div style={{ display: "flex", gap: "8px" }}>
                <select
                  value={capRateMin}
                  onChange={(e) => setCapRateMin(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "6px 8px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "13px",
                    backgroundColor: "white"
                  }}
                >
                  <option value="">From</option>
                  {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(v => (
                    <option key={v} value={v}>{v}%</option>
                  ))}
                </select>
                <select
                  value={capRateMax}
                  onChange={(e) => setCapRateMax(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "6px 8px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "13px",
                    backgroundColor: "white"
                  }}
                >
                  <option value="">To</option>
                  {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(v => (
                    <option key={v} value={v}>{v}%</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Days on Market */}
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" }}>
                Days on Market
              </label>
              <input
                type="number"
                placeholder="Max days"
                min="0"
                value={daysOnMarketMax}
                onChange={(e) => setDaysOnMarketMax(e.target.value)}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  fontSize: "13px"
                }}
              />
            </div>

            {/* Carrying Costs (HOA + Tax) */}
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" }}>
                HOA + Tax (max)
              </label>
              <input
                type="number"
                placeholder="e.g. 1000"
                min="0"
                value={carryingCostMax}
                onChange={(e) => setCarryingCostMax(e.target.value)}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  fontSize: "13px"
                }}
              />
            </div>

            {/* Date Listed */}
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" }}>
                Date Listed
              </label>
              <div style={{ display: "flex", gap: "8px" }}>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "6px 8px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "13px"
                  }}
                />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "6px 8px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontSize: "13px"
                  }}
                />
              </div>
            </div>

            {/* Sort By */}
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" }}>
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  fontSize: "13px",
                  backgroundColor: "white"
                }}
              >
                <option value="date_desc">Newest First</option>
                <option value="date_asc">Oldest First</option>
                <option value="cap_desc">Cap Rate (High to Low)</option>
                <option value="cap_asc">Cap Rate (Low to High)</option>
                <option value="price_asc">Price (Low to High)</option>
                <option value="price_desc">Price (High to Low)</option>
                <option value="dom_asc">DOM (Low to High)</option>
                <option value="dom_desc">DOM (High to Low)</option>
              </select>
            </div>

            {/* Search + Reset Buttons - aligned at bottom */}
            <div style={{ display: "flex", alignItems: "flex-end", gap: "8px", gridColumn: "span 3" }}>
              <button
                onClick={loadReviews}
                disabled={loading}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#000",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: loading ? "wait" : "pointer"
                }}
              >
                {loading ? "Loading..." : "Search"}
              </button>
              <button
                onClick={handleClearFilters}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer"
                }}
              >
                Reset
              </button>
              <div style={{ fontSize: "13px", color: "#666", fontWeight: "500", padding: "8px 0" }}>
                {filteredReviews.length} of {totalCount} properties
                {totalCount > reviews.length && (
                  <span style={{ fontStyle: "italic", marginLeft: "4px" }}>
                    (showing {reviews.length} latest)
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div style={boardStyle}>
          {STATUSES.map((status) => (
            <div
              key={`${status.key}-${filteredReviews.length}`}
              style={columnStyle(status.color)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, status.key)}
            >
              <div style={columnHeaderStyle(status.color)}>
                <span>{status.label}</span>
                <span style={badgeStyle}>
                  {groupedReviews[status.key]?.length || 0}
                </span>
              </div>

              {groupedReviews[status.key]?.map((review) => (
                <div
                  key={review.id}
                  style={cardStyle}
                  draggable
                  onDragStart={(e) => handleDragStart(e, review)}
                  onMouseEnter={(e) => {
                    Object.assign(e.currentTarget.style, cardHoverStyle);
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "";
                    e.currentTarget.style.boxShadow =
                      "0 1px 3px rgba(0,0,0,0.1)";
                  }}
                >
                  <div style={addressStyle}>
                    {review.property.address}
                    {review.property.unit_number && ` #${review.property.unit_number}`}
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        // Addresses can carry padded/trailing whitespace — collapse it so the copy is clean.
                        const addr = (review.property.address || "").replace(/\s+/g, " ").trim();
                        const unit = (review.property.unit_number || "").trim();
                        navigator.clipboard?.writeText(`${addr}${unit ? ` #${unit}` : ""}`);
                      }}
                      title="Copy address"
                      style={{ cursor: "pointer", marginLeft: 6, fontSize: 12, opacity: 0.55 }}
                    >📋</span>
                  </div>
                  {review.property.property_type && (
                    <span style={{ display: "inline-block", fontSize: "11px", fontWeight: 600, color: "#555",
                                   border: "1px solid #ddd", borderRadius: "4px", padding: "1px 6px", marginBottom: "4px" }}>
                      {["COOP", "CONDP"].includes((review.property.property_type || "").toUpperCase())
                        ? "Co-op"
                        : (review.property.property_type || "").toUpperCase() === "CONDO"
                          ? "Condo"
                          : review.property.property_type}
                    </span>
                  )}
                  <div style={detailsStyle}>
                    {review.property.neighborhood},{" "}
                    {review.property.borough}
                  </div>
                  {review.property.listed_at && (
                    <div style={detailsStyle}>
                      Listed: {new Date(review.property.listed_at).toLocaleDateString()}
                    </div>
                  )}
                  <div style={detailsStyle}>
                    {review.property.bedrooms !== null ? review.property.bedrooms : "?"} bed •{" "}
                    {review.property.bathrooms || "?"} bath •{" "}
                    {review.property.sqft || "?"} sqft
                  </div>
                  {review.property.price && (
                    <div style={getPriceStyle(review.property.price_change)}>
                      ${review.property.price.toLocaleString()}
                      {formatPriceChange(review.property.price_change, review.property.price_change_pct) && (
                        <span style={{ fontSize: "12px", fontWeight: "500", marginLeft: "6px" }}>
                          {formatPriceChange(review.property.price_change, review.property.price_change_pct)}
                        </span>
                      )}
                    </div>
                  )}
                  {review.property.cap_rate && (
                    <div style={{ fontSize: "14px", fontWeight: "600", color: "#1976d2", marginTop: "4px" }}>
                      Cap Rate: {review.property.cap_rate.toFixed(2)}%
                      {review.property.neighborhood_avg_cap_rate && (
                        <span style={{ fontSize: "12px", fontWeight: "400", color: "#666", marginLeft: "8px" }}>
                          (Neighborhood avg: {review.property.neighborhood_avg_cap_rate.toFixed(2)}%)
                        </span>
                      )}
                    </div>
                  )}
                  {review.property.below_building_sale && (
                    <div style={{
                      fontSize: "12px",
                      fontWeight: "700",
                      color: "#e65100",
                      backgroundColor: "#fff3e0",
                      padding: "3px 8px",
                      borderRadius: "4px",
                      marginTop: "6px",
                      border: "1px solid #ffcc80",
                      display: "inline-block",
                    }}
                    title={`Match: ${review.property.below_building_sale.match_type === 'same_line' ? 'Same line' : 'Same bed/bath/sqft'} | Sold: $${review.property.below_building_sale.sold_price?.toLocaleString()} (${review.property.below_building_sale.sold_date?.slice(0, 7) || '?'})`}
                    >
                      {review.property.below_building_sale.pct_below}% below sold #{review.property.below_building_sale.sold_unit}
                    </div>
                  )}
                  {review.status_reason && (
                    <div style={{ fontSize: "12px", color: "#555", marginTop: "6px", padding: "6px 8px", backgroundColor: "#f5f5f5", borderRadius: "4px", borderLeft: "3px solid #1976d2", fontStyle: "italic" }}>
                      {review.status_reason}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                    <button
                      style={{
                        padding: "4px 8px",
                        fontSize: "11px",
                        backgroundColor: "#1976d2",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`/predict/${review.sale_listing_id}`, '_blank');
                      }}
                    >
                      View Analysis
                    </button>
                    <button
                      style={{
                        padding: "4px 8px",
                        fontSize: "11px",
                        backgroundColor: "#7b1fa2",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`/comps/${review.sale_listing_id}`, '_blank');
                      }}
                    >
                      Comps
                    </button>
                    {review.property.listing_url && (
                      <button
                        style={{
                          padding: "4px 8px",
                          fontSize: "11px",
                          backgroundColor: "#2e7d32",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(review.property.listing_url, '_blank');
                        }}
                      >
                        Web Listing
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Modal for MIS/Retired comment */}
      {showModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: "#fff",
            borderRadius: "8px",
            padding: "24px",
            maxWidth: "500px",
            width: "90%",
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)"
          }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", color: "#333" }}>
              {pendingStatus === "MIS" ? "Mark as MIS" : "Mark as Retired"}
            </h3>
            <p style={{ margin: "0 0 16px 0", fontSize: "14px", color: "#666" }}>
              Please provide a reason for this status change (required):
            </p>
            <textarea
              value={statusComment}
              onChange={(e) => setStatusComment(e.target.value)}
              placeholder="Enter reason..."
              style={{
                width: "100%",
                minHeight: "100px",
                padding: "12px",
                fontSize: "14px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontFamily: "inherit",
                resize: "vertical",
                boxSizing: "border-box"
              }}
              autoFocus
            />
            <div style={{ display: "flex", gap: "12px", marginTop: "20px", justifyContent: "flex-end" }}>
              <button
                onClick={handleModalCancel}
                disabled={submitting}
                style={{
                  padding: "10px 20px",
                  fontSize: "14px",
                  fontWeight: "600",
                  backgroundColor: "#f5f5f5",
                  color: "#333",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  cursor: submitting ? "not-allowed" : "pointer",
                  opacity: submitting ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleModalConfirm}
                disabled={!statusComment.trim() || submitting}
                style={{
                  padding: "10px 20px",
                  fontSize: "14px",
                  fontWeight: "600",
                  backgroundColor: pendingStatus === "MIS" ? "#4caf50" : "#ff9800",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: (!statusComment.trim() || submitting) ? "not-allowed" : "pointer",
                  opacity: (!statusComment.trim() || submitting) ? 0.5 : 1
                }}
              >
                {submitting ? "Updating..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
